import { useEffect, useState } from 'react';
import { fetchNews } from '../api';
import { pillarColor } from '../constants';
import { navLabel } from '../i18n';
import { useSettings } from '../settings';
import type { ArticleDTO } from '../types';
import ArticleCard from './ArticleCard';
import Popular from './Popular';

type FeedState =
  | { status: 'loading' }
  | { status: 'ready'; articles: ArticleDTO[] }
  | { status: 'error'; message: string };

interface FeedProps {
  pillar: string;
  subsection: string | null;
  activeRegion: string | null;
}

function Skeleton() {
  return (
    <div className="animate-pulse border-t border-rule pt-4">
      <div className="h-2 w-24 rounded bg-rule" />
      <div className="mt-3 h-8 w-4/5 rounded bg-rule" />
      <div className="mt-2 h-8 w-3/5 rounded bg-rule" />
      <div className="mt-4 h-3 w-full rounded bg-rule/70" />
      <div className="mt-2 h-3 w-11/12 rounded bg-rule/70" />
      <div className="mt-2 h-3 w-9/12 rounded bg-rule/70" />
    </div>
  );
}

export default function Feed({ pillar, subsection, activeRegion }: FeedProps) {
  const { t, locale } = useSettings();
  const [state, setState] = useState<FeedState>({ status: 'loading' });
  const [popular, setPopular] = useState<ArticleDTO[]>([]);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    fetchNews({
      pillar,
      subsection,
      region: activeRegion,
      locale,
      limit: 40,
      signal: controller.signal,
    })
      .then((articles) => setState({ status: 'ready', articles }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : t('wireUnavailable'),
        });
      });

    return () => controller.abort();
  }, [pillar, subsection, activeRegion, locale, attempt, t]);

  useEffect(() => {
    const controller = new AbortController();
    fetchNews({ pillar, locale, sort: 'popular', limit: 5, signal: controller.signal })
      .then(setPopular)
      .catch(() => setPopular([]));
    return () => controller.abort();
  }, [pillar, locale]);

  const color = pillarColor(pillar);
  const heading = subsection ? navLabel(locale, subsection) : navLabel(locale, pillar);
  const articles = state.status === 'ready' ? state.articles : [];
  const [lead, ...rest] = articles;
  const rail = rest.slice(0, 2);
  const grid = rest.slice(2, 10);

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 pb-12 sm:px-6">
      <div className="flex items-end justify-between gap-4 pb-2 pt-6">
        <h1 className="font-serif text-2xl font-bold tracking-[-0.01em] text-ink sm:text-3xl">
          {heading}
        </h1>
        <span className="pb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim">
          {state.status === 'ready' ? `${articles.length} ${t('dispatches')}` : t('loading')}
        </span>
      </div>
      <div className="h-[3px] w-full" style={{ backgroundColor: color }} />

      {state.status === 'loading' && (
        <div className="mt-6 grid gap-x-8 gap-y-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <Skeleton />
          </div>
          <div className="md:col-span-5 space-y-6">
            <Skeleton />
            <Skeleton />
          </div>
        </div>
      )}

      {state.status === 'error' && (
        <div className="mt-8 border border-rule p-6">
          <p className="font-serif text-xl text-ink">{t('wireUnavailable')}</p>
          <p className="mt-1 text-sm text-ink-muted">{state.message}</p>
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
            className="mt-4 border border-rule px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft hover:border-rule-strong"
          >
            {t('retry')}
          </button>
        </div>
      )}

      {state.status === 'ready' && articles.length === 0 && (
        <div className="mt-8 border-t border-rule pt-6">
          <p className="font-serif text-xl text-ink">{t('emptyWire')}</p>
          <p className="mt-1 text-sm text-ink-muted">{t('emptyDesk')}</p>
        </div>
      )}

      {state.status === 'ready' && lead && (
        <>
          <div className="mt-6 grid gap-x-8 gap-y-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <ArticleCard article={lead} variant="lead" />
            </div>

            <div className="flex flex-col gap-6 md:col-span-5 md:border-l md:border-rule md:pl-6">
              {rail.map((article) => (
                <ArticleCard key={article.id} article={article} variant="rail" />
              ))}
              <Popular items={popular} />
            </div>
          </div>

          {grid.length > 0 && (
            <div className="mt-10 grid gap-x-8 gap-y-8 border-t border-rule pt-6 sm:grid-cols-2 lg:grid-cols-4">
              {grid.map((article) => (
                <ArticleCard key={article.id} article={article} variant="grid" />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
